/**
 * Role Utility
 * Centralized utility functions for determining user roles and checking role permissions
 */

import { AuthUser } from '../auth/authService';
import type { User } from '../types/user.types';

/**
 * User role types
 */
export type UserRoleType =
  | 'SuperAdmin'
  | 'Admin'
  | 'Technician'
  | 'Radiologist'
  | 'ReferringPhysician';

/**
 * Role definitions for easy reference
 * These are the technical role names used in the authentication system
 */
export const ROLES = {
  SUPER_ADMIN: 'ris-super-admin', // Superadmin is identified by ris-super-admin role in realm_access.roles
  RIS_ADMIN: 'ris-admin', // RIS Admin role
  RIS_TECHNICIAN: 'ris-technician', // RIS Technician role
  RIS_RADIOLOGIST: 'ris-radiologist', // RIS Radiologist role
  RIS_DISTRICT_ADMIN: 'ris-district-admin',
  RIS_HUB_ADMIN: 'ris-hub-admin',
  RIS_RADIOLOGY_MANAGER: 'ris-radiology-manager',
  RIS_REFERRING_PHYSICIAN: 'ris-referring-physician', // RIS Referring Physician role
  RIS_QUALITY_MANAGER: 'ris-quality-manager', // RIS Quality Manager role
  ADMIN: 'admin',
  VIEW: 'ris-view',
  CREATE: 'ris-create',
  UPDATE: 'ris-update',
  DELETE: 'ris-delete',
  APPROVE: 'ris-approve',
  // Display role names for UI comparisons
  USE_SUPER_ADMIN: 'SuperAdmin',
  USE_ADMIN: 'Admin',
  USE_TECHNICIAN: 'Technician',
  USE_RADIOLOGIST: 'Radiologist',
  USE_REFERRING_PHYSICIAN: 'ReferringPhysician',
} as const;

/**
 * Determine user role from AuthUser object
 * Maps technical role names to display names
 * ris-super-admin = SuperAdmin
 * ris-admin = Admin
 * ris-radiologist = Radiologist
 * ris-technician = Technician
 * @throws Error if user is null - authentication must be verified before calling this function
 */
export function getUserRoleFromAuthUser(user: AuthUser | null): UserRoleType {
  if (!user) {
    return 'Admin'; // Safe default when no user (e.g. mock/dev bypass)
  }
  // Get roles from realm_access.roles first (most reliable source)
  const realmAccessRoles = user.realm_access?.roles || [];
  // Also get flattened roles array as fallback
  const roles = user.roles || [];

  // Check for SuperAdmin - first check realm_access.roles
  if (realmAccessRoles.some((r: string) => r === ROLES.SUPER_ADMIN)) {
    return 'SuperAdmin';
  }
  // Fallback to flattened roles array
  if (roles.some(r => r === ROLES.SUPER_ADMIN)) {
    return 'SuperAdmin';
  }
  // Legacy check for realm-admin in resource_access (for backward compatibility)
  if (user.resource_access) {
    const realmManagement = user.resource_access['realm-management'];
    if (realmManagement?.roles?.some((r: string) => r === 'realm-admin')) {
      return 'SuperAdmin';
    }
  }
  // Legacy check for realm-admin in roles array (for backward compatibility)
  if (roles.some(r => r === 'realm-admin')) {
    return 'SuperAdmin';
  }

  // Check for Admin - first check realm_access.roles
  if (realmAccessRoles.some((r: string) => r === 'ris-admin')) {
    return 'Admin';
  }
  // Fallback to flattened roles array
  if (roles.some(r => r === 'ris-admin')) {
    return 'Admin';
  }

  // Check for Radiologist - first check realm_access.roles
  if (realmAccessRoles.some((r: string) => r === 'ris-radiologist')) {
    return 'Radiologist';
  }
  // Fallback to flattened roles array
  if (roles.some(r => r === 'ris-radiologist')) {
    return 'Radiologist';
  }

  // Check for Technician - first check realm_access.roles
  if (realmAccessRoles.some((r: string) => r === 'ris-technician')) {
    return 'Technician';
  }
  // Fallback to flattened roles array
  if (roles.some(r => r === 'ris-technician')) {
    return 'Technician';
  }

  // Check for Referring Physician - first check realm_access.roles
  if (realmAccessRoles.some((r: string) => r === 'ris-referring-physician')) {
    return 'ReferringPhysician';
  }
  // Fallback to flattened roles array
  if (roles.some(r => r === 'ris-referring-physician')) {
    return 'ReferringPhysician';
  }

  return 'Admin'; // Default
}

/**
 * Determine user role from roles array and database user data
 * This function checks both the database user object and the roles array
 * Priority: is_superadmin flag > database role field > roles array
 */
function getRoleFromDatabase(
  fromDatabase?: User & { is_superadmin?: boolean }
): UserRoleType | null {
  if (fromDatabase?.is_superadmin === true) {
    return 'SuperAdmin';
  }

  if (!fromDatabase?.role) {
    return null;
  }

  // Note: fromDatabase.role is typed as UserRole ('Technician' | 'Radiologist') but may contain other values
  const dbRole = fromDatabase.role as string;
  if (dbRole === 'SuperAdmin' || dbRole === 'Super Admin') {
    return 'SuperAdmin';
  }
  if (dbRole === 'Admin') {
    return 'Admin';
  }
  if (dbRole === 'Radiologist') {
    return 'Radiologist';
  }
  if (dbRole === 'Technician') {
    return 'Technician';
  }
  return null;
}

function getRoleFromRolesArray(roles: string[]): UserRoleType {
  // Check for superadmin in roles - check for ris-super-admin first
  if (roles.some(r => r === ROLES.SUPER_ADMIN)) {
    return 'SuperAdmin';
  }

  // Legacy checks for backward compatibility
  if (
    roles.some(
      r => r.toLowerCase().includes('superadmin') || r === 'ris-delete' || r === 'realm-admin'
    )
  ) {
    return 'SuperAdmin';
  }

  if (roles.some(r => r === 'ris-admin')) {
    return 'Admin';
  }

  if (roles.some(r => r === 'ris-radiologist')) {
    return 'Radiologist';
  }

  if (roles.some(r => r === 'ris-technician')) {
    return 'Technician';
  }

  return 'Admin';
}

export function getUserRole(
  roles: string[] | undefined,
  fromDatabase?: User & { is_superadmin?: boolean }
): UserRoleType {
  const dbRole = getRoleFromDatabase(fromDatabase);
  if (dbRole) {
    return dbRole;
  }

  // Fallback to checking roles array
  if (!roles || roles.length === 0) {
    return 'Admin';
  }

  return getRoleFromRolesArray(roles);
}

/**
 * Get user role display name (alias for getUserRoleFromAuthUser)
 * This is a convenience function that returns the role as a string for display purposes
 */
export function getUserRoleDisplayName(user: AuthUser | null): string {
  return getUserRoleFromAuthUser(user);
}

/**
 * ============================================================================
 * ROLE CHECKING FUNCTIONS
 * ============================================================================
 * Functions to check if a user has specific roles for permission checks
 */

/**
 * Check if user has a specific role
 * Checks realm_access.roles first for all RIS roles, then falls back to flattened roles array
 */
function logRoleFound(source: string, role: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.log(`hasRole - Found ${role} in ${source}`);
  }
}

function hasRoleInRealmAccess(user: AuthUser, role: string): boolean {
  if (!user.realm_access?.roles || !Array.isArray(user.realm_access.roles)) {
    return false;
  }
  if (!user.realm_access.roles.includes(role)) {
    return false;
  }
  logRoleFound('realm_access.roles', role);
  return true;
}

function hasRoleInRolesArray(user: AuthUser, role: string): boolean {
  if (!user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  if (!user.roles.includes(role)) {
    return false;
  }
  logRoleFound('user.roles array', role);
  return true;
}

function hasRealmAdminInResourceAccess(user: AuthUser): boolean {
  if (!user.resource_access) {
    return false;
  }
  const realmManagement = user.resource_access['realm-management'];
  if (!realmManagement?.roles || !Array.isArray(realmManagement.roles)) {
    return false;
  }
  if (!realmManagement.roles.includes('realm-admin')) {
    return false;
  }
  logRoleFound('resource_access.realm-management.roles', 'realm-admin');
  return true;
}

function hasRoleInAnyResourceAccess(user: AuthUser, role: string): boolean {
  if (!user.resource_access || typeof user.resource_access !== 'object') {
    return false;
  }
  for (const access of Object.values(user.resource_access)) {
    const entry = access as { roles?: string[] } | null | undefined;
    if (entry?.roles?.includes(role)) {
      return true;
    }
  }
  return false;
}

export function hasRole(user: AuthUser | null, role: string): boolean {
  if (!user) {
    return false;
  }

  if (hasRoleInRealmAccess(user, role)) {
    return true;
  }

  if (hasRoleInRolesArray(user, role)) {
    return true;
  }

  // Legacy check for realm-admin in resource_access (for backward compatibility)
  if (role === 'realm-admin' && hasRealmAdminInResourceAccess(user)) {
    return true;
  }

  // Keycloak: `ris-super-admin` and other RIS roles are sometimes only on client `resource_access`
  if (hasRoleInAnyResourceAccess(user, role)) {
    return true;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`hasRole - ${role} NOT found`, {
      userRoles: user.roles,
      hasRealmAccess: !!user.realm_access,
      realmAccessRoles: user.realm_access?.roles,
      hasResourceAccess: !!user.resource_access,
      realmManagement: user.resource_access?.['realm-management'],
    });
  }

  return false;
}

/**
 * Check if user has any of the specified roles
 * Uses hasRole to properly check both roles array and resource_access
 */
export function hasAnyRole(user: AuthUser | null, roles: string[]): boolean {
  if (!user || roles.length === 0) {
    return false;
  }
  return roles.some(role => hasRole(user, role));
}

/**
 * Check if user has all of the specified roles
 * Uses hasRole to properly check both roles array and resource_access
 */
export function hasAllRoles(user: AuthUser | null, roles: string[]): boolean {
  if (!user || roles.length === 0) {
    return false;
  }
  return roles.every(role => hasRole(user, role));
}

/**
 * Check if user is Technician or Admin
 * Returns true if user has ris-technician or ris-admin role
 */
export function isTechnicianOrAdmin(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }
  return hasAnyRole(user, [ROLES.RIS_TECHNICIAN, ROLES.RIS_ADMIN]);
}

/**
 * Referring physician: same Imaging Worklist chrome as admin/tech, without assignment UI.
 */
export function isReferringPhysician(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }
  return hasRole(user, ROLES.RIS_REFERRING_PHYSICIAN);
}

/**
 * Show the Assigned column on the imaging worklist (admin/tech can assign; referring physicians read-only).
 */
export function showImagingWorklistAssignedColumn(user: AuthUser | null): boolean {
  return isTechnicianOrAdmin(user) || isReferringPhysician(user);
}

/**
 * Show hub/spoke facility column on the imaging worklist (RIS admin/technician at HUB or SPOKE only).
 */
export function showImagingWorklistHubSpokeColumn(
  user: AuthUser | null,
  facilityType?: string | null
): boolean {
  if (!isTechnicianOrAdmin(user)) {
    return false;
  }

  const normalized = String(facilityType || '')
    .trim()
    .toUpperCase();
  return normalized === 'HUB' || normalized === 'SPOKE';
}

export type ImagingWorklistLayout = 'classic' | 'adminTech' | 'radiologist';

/**
 * Studies / worklist chrome: Figma "Imaging Worklist" for RIS technician, admin, and referring physician (adminTech)
 * and for radiologist (radiologist). Everyone else keeps the classic OHIF-style list.
 * If a user has ris-radiologist among their roles, radiologist layout wins.
 * Referring physicians use adminTech without assign/checkbox UI (see isTechnicianOrAdmin in WorkList).
 */
export function getImagingWorklistLayout(user: AuthUser | null): ImagingWorklistLayout {
  if (!user) {
    return 'classic';
  }
  if (hasRole(user, ROLES.RIS_RADIOLOGIST)) {
    return 'radiologist';
  }
  if (hasAnyRole(user, [ROLES.RIS_ADMIN, ROLES.RIS_TECHNICIAN, ROLES.RIS_REFERRING_PHYSICIAN])) {
    return 'adminTech';
  }
  return 'classic';
}

/**
 * Check if user role is Radiologist
 * @param role - The user role type to check
 * @returns true if role is Radiologist
 */
export function isRadiologist(role: UserRoleType): boolean {
  return role === ROLES.USE_RADIOLOGIST;
}

/**
 * Get RIS role string from AuthUser
 * Returns the technical RIS role string (e.g., 'ris-admin', 'ris-radiologist') from the user's roles
 * @param user - The AuthUser object
 * @returns The RIS role string or undefined if not found
 */
export function getRISRoleString(user: AuthUser | null): string | undefined {
  if (!user) {
    return undefined;
  }

  // Get roles from realm_access.roles first (most reliable source)
  const realmAccessRoles = user.realm_access?.roles || [];
  // Also get flattened roles array as fallback
  const roles = user.roles || [];

  // Check for RIS roles in order of priority
  const risRoles = [
    ROLES.SUPER_ADMIN,
    ROLES.RIS_ADMIN,
    ROLES.RIS_RADIOLOGIST,
    ROLES.RIS_TECHNICIAN,
  ];

  // First check realm_access.roles
  for (const risRole of risRoles) {
    if (realmAccessRoles.includes(risRole)) {
      return risRole;
    }
  }

  // Fallback to flattened roles array
  for (const risRole of risRoles) {
    if (roles.includes(risRole)) {
      return risRole;
    }
  }

  return undefined;
}

/**
 * ============================================================================
 * PATIENT ROLE CHECKING
 * ============================================================================
 */

/**
 * Patient role identifier
 */
export const PATIENT_ROLE = 'patient';

/**
 * Check if user is a patient based on their role
 * This is the single source of truth for patient role checking
 * Checks both realm_access.roles and regular roles array
 */
export function isPatient(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }

  // Check if user has 'patient' role in realm_access.roles
  if (user.realm_access?.roles && Array.isArray(user.realm_access.roles)) {
    if (user.realm_access.roles.includes(PATIENT_ROLE)) {
      return true;
    }
  }

  // Check if user has 'patient' role in regular roles array
  if (user.roles && Array.isArray(user.roles)) {
    if (user.roles.includes(PATIENT_ROLE)) {
      return true;
    }
  }

  return false;
}
